import {
  INTENT_LIMITS,
  newRequirementEntry,
  REQUIREMENT_GROUPS,
  requirementGroup,
  requirementGroupLabel,
  type RequirementEntry,
} from '@blueprint/domain'
import { useId, useState } from 'react'
import { Button, IconButton, TextArea, TextInput } from '../../../components/controls.js'
import { useUiStore } from '../../../state/ui-store.js'
import styles from './intent.module.css'

interface Props {
  entries: readonly RequirementEntry[]
  onChange: (next: RequirementEntry[]) => void
}

/**
 * Human-authored requirement groups. The catalogue offers writing prompts — questions
 * to answer — and only what the user types is ever carried into a prompt.
 */
export function RequirementEntriesEditor({ entries, onChange }: Props) {
  const [group, setGroup] = useState(REQUIREMENT_GROUPS[0]?.key ?? 'other')
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  const selectId = useId()

  const update = (id: string, patch: Partial<RequirementEntry>) =>
    onChange(entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)))

  const remove = (entry: RequirementEntry) => {
    const drop = () => onChange(entries.filter((item) => item.id !== entry.id))
    const hasContent = entry.heading.trim().length > 0 || entry.body.trim().length > 0
    if (!hasContent) {
      drop()
      return
    }
    // Removing written content asks first; excluding it from a prompt does not.
    requestConfirm({
      title: 'Remove context entry',
      message: `"${entry.heading.trim() || requirementGroupLabel(entry.group)}" and everything written in it will be removed from this card.`,
      consequence: 'To keep the text but leave it out of a prompt, clear "Include in prompt" instead.',
      confirmLabel: 'Remove',
      undoable: true,
      onConfirm: drop,
    })
  }

  const atLimit = entries.length >= INTENT_LIMITS.requirementCount

  return (
    <div className={styles.stack}>
      <div className={styles.adder}>
        <div>
          <label className={styles.entryGroup} htmlFor={selectId}>
            Add context
          </label>
          <br />
          <select
            id={selectId}
            className={styles.adderSelect}
            value={group}
            onChange={(event) => setGroup(event.target.value)}
          >
            {REQUIREMENT_GROUPS.map((definition) => (
              <option key={definition.key} value={definition.key}>
                {definition.label}
              </option>
            ))}
          </select>
        </div>
        <Button disabled={atLimit} onClick={() => onChange([...entries, newRequirementEntry(group)])}>
          Add
        </Button>
        {atLimit ? (
          <span className={styles.locatorNote}>Limit of {INTENT_LIMITS.requirementCount} entries reached.</span>
        ) : null}
      </div>

      {entries.length === 0 ? (
        <p className={styles.empty}>
          No context added. Pick the groups relevant to this work — a new form might need UI, layout, fields,
          validation and component context at once. You can add more than one entry per group.
        </p>
      ) : null}

      {entries.map((entry, index) => {
        const definition = requirementGroup(entry.group)
        const label = definition?.label ?? entry.group
        return (
          <section key={entry.id} className={styles.entry}>
            <header className={styles.entryHead}>
              <span className={styles.entryGroup}>{label}</span>
              <TextInput
                value={entry.heading}
                aria-label={`Heading for ${label} entry ${index + 1}`}
                placeholder="Name this entry, e.g. Address form"
                maxLength={INTENT_LIMITS.requirementHeading}
                onChange={(event) => update(entry.id, { heading: event.target.value })}
              />
              <label className={styles.include}>
                <input
                  type="checkbox"
                  checked={entry.include}
                  onChange={(event) => update(entry.id, { include: event.target.checked })}
                />
                Include in prompt
              </label>
              <IconButton danger aria-label={`Remove ${label} entry ${index + 1}`} onClick={() => remove(entry)}>
                &#10005;
              </IconButton>
            </header>
            <div className={styles.entryBody}>
              {definition ? <p className={styles.guidance}>{definition.guidance}</p> : null}
              <TextArea
                value={entry.body}
                aria-label={`Details for ${label} entry ${index + 1}`}
                maxLength={INTENT_LIMITS.requirementBody}
                placeholder="Your requirements for this card"
                onChange={(event) => update(entry.id, { body: event.target.value })}
              />
            </div>
          </section>
        )
      })}
    </div>
  )
}
